import { useNavigate, useParams } from 'react-router-dom';
import { useListingStore } from '../store/listingStore';
import { generateTitle, generateDescription, updateListing, triggerPriceResearch } from '../lib/api';
import { useStepAction } from '../hooks/useStepAction';

const CONDITIONS = [
  'New',
  'New other (see details)',
  'Manufacturer refurbished',
  'Seller refurbished',
  'Used — like new',
  'Used — good',
  'Used — acceptable',
  'For parts or not working',
];

export default function Step3Details() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useListingStore();

  const canProceed = !!store.condition;
  useStepAction('Next: Pricing \u2192', !canProceed, handleNext);
  // Step 4 in the wizard (was step 3 before Aspects was inserted)

  async function handleNext() {
    if (!id || !canProceed) return;
    // Save details (including specialNotes) to DB first — pricing research reads them
    await updateListing(id, {
      itemCondition: store.condition,
      specialNotes: store.specialNotes,
    });

    // Now that specialNotes is in the DB, kick off pricing research.
    // Mark as QUEUED immediately so Step 5 doesn't double-trigger it.
    store.setPricingJobStatus('QUEUED');
    triggerPriceResearch(id).catch(() => store.setPricingJobStatus('FAILED'));

    // Kick off title + description generation now — all data is in DB (identification,
    // condition, specialNotes, category). They run while the user fills out pricing so
    // step 6 arrives to pre-populated fields with no wait.
    store.setGenerationStatus('prefetching');
    generateTitle(id)
      .then(({ title }) => {
        store.setTitleSuggestion(title);
        if (!useListingStore.getState().itemTitle) store.setItemTitle(title);
      })
      .catch(() => {});
    generateDescription(id)
      .then(({ description }) => {
        store.setDescriptionSuggestion(description);
        store.setGenerationStatus('done');
      })
      .catch(() => {
        store.setGenerationStatus('idle'); // fall back to on-demand gen in step 6
      });

    store.setCurrentStep(5);
    navigate(`/listing/${id}/step/5`);
  }

  return (
    <div className="space-y-8 max-w-lg">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Item details</h2>
        <p className="text-gray-500 mt-1">Tell buyers about the condition and appearance.</p>
      </div>

      {/* Condition */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Condition <span className="text-red-500">*</span>
        </label>
        <select
          value={store.condition}
          onChange={(e) => store.setCondition(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
        >
          <option value="">Select condition…</option>
          {CONDITIONS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Special notes */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Anything the buyer should know?
        </label>
        <textarea
          value={store.specialNotes}
          onChange={(e) => store.setSpecialNotes(e.target.value)}
          rows={4}
          placeholder="Scratches, missing parts, original box included, etc."
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
        <p className="text-xs text-gray-400 mt-1">Optional — this text feeds into the description generation.</p>
      </div>

      <div className="flex justify-between pt-2">
      </div>
    </div>
  );
}
